import React from 'react';
import { formatMoney } from '@src/utilities/numberFormattingUtility';

export enum ProgramAttributes {
	'Location' = 'Location',
	'Type' = 'Type',
	'Effort' = 'Effort',
	'Funding' = 'Funding',
	'Next start date' = 'Next start date',
	'Length' = 'Length',
	'Guild Coaching' = 'Guild Coaching',
	'Interest' = 'Interest',
	'Location requirements' = 'Location requirements',
	'Prerequisites' = 'Prerequisites',
	'Skill requirements' = 'Skill requirements',
	'Other requirements' = 'Other requirements',
	'Work experience requirements' = 'Work experience requirements',
	'Technology requirements' = 'Technology requirements',
	'GPA requirements' = 'GPA requirements',
}

export const highlightedAttributes = [
	ProgramAttributes.Location,
	ProgramAttributes.Type,
	ProgramAttributes.Effort,
	ProgramAttributes.Funding,
	ProgramAttributes['Next start date'],
	ProgramAttributes.Length,
	ProgramAttributes['Guild Coaching'],
];

export const getProgramFundingMetadata = (fundingLevel: number) => {
	switch (fundingLevel) {
		case -1:
			return 'Your employer will pay 100% of your tuition costs directly to your school. If applicable, grants and scholarships are applied to tuition and fees before employer funding.';
		default:
			return `Your employer will pay up to ${formatMoney(
				fundingLevel
			)} of your tuition costs directly to your school each year.`;
	}
};

export const getDppFundingMetadata = (fundingLevel: number) => {
	switch (fundingLevel) {
		case -1:
			return "You'll apply for a Sponsorship Letter that tells your school to expect 100% tuition payment from your employer.";
		default:
			return `You'll apply for a Sponsorship Letter that tells your school to expect payment from your employer, up to ${formatMoney(
				fundingLevel
			)} each year.`;
	}
};

export const getProgramTypeMetadata = (programType: string) => {
	switch (programType) {
		case 'ASSOCIATES':
			return {
				body: 'Associate degree',
				popoverCopy:
					'An associate degree provides entry-level training in a specific career field.',
			};
		case 'BACHELORS':
			return {
				body: "Bachelor's degree",
				popoverCopy:
					"A bachelor's degree provides broad learning in a range of subjects, and focused study in one area.",
			};
		case 'COLLEGE_PREP':
			return {
				body: 'College Start',
				popoverCopy:
					'College Start lets you prepare for entering a degree program while earning real college credit.',
			};
		case 'DOCTORAL':
			return {
				body: 'Doctoral degree',
				popoverCopy:
					"A doctoral degree provides highly advanced study in a specialized subject, and requires a bachelor's or master's degree.",
			};
		case 'HIGH_SCHOOL':
			return {
				body: 'High School Diploma or Equivalency',
				popoverCopy:
					'High school diploma or equivalency prepares you for future work or study, and helps you access work or education opportunities.',
			};
		case 'LANGUAGE':
			return {
				body: 'Language',
				popoverCopy:
					'Language programs help you develop skills in using a language for everyday life or for specific purposes.',
			};
		case 'MASTERS':
			return {
				body: "Master's degree",
				popoverCopy:
					"A master's degree provides advanced study in a specific subject or professional area.",
			};
		default:
			return {
				body: '',
				popoverCopy: '',
			};
	}
};

export const getCertificateMetadata = (certificateType: string) => {
	switch (certificateType) {
		case 'INTRODUCTORY_CERTIFICATE':
			return {
				body: 'Introductory certificate',
				popoverCopy:
					'An introductory certificate or bootcamp is a short education or training program that requires no prior experience.',
			};
		case 'INTERMEDIATE_CERTIFICATE':
			return {
				body: 'Intermediate certificate',
				popoverCopy:
					"An intermediate certificate or bootcamp is a short program that provides further training on skills you've begun to develop.",
			};
		case 'ADVANCED_CERTIFICATE':
			return {
				body: 'Advanced certificate',
				popoverCopy:
					"An advanced certificate or bootcamp is a short program that provides training to make you even better at skills you've already built.",
			};
		default:
			return {
				body: '',
				popoverCopy: '',
			};
	}
};

export const getProgramFormatMetadata = (programFormat: string) => {
	switch (programFormat) {
		case 'Online Program':
			return {
				body: 'Online',
				popoverCopy: 'The program is delivered 100% on the internet.',
			};
		case 'online_or_in_person':
			return {
				body: 'Online or in-person',
				popoverCopy:
					'The program is delivered either on the internet or onsite at learning provider or other site.',
			};
		case 'online_and_in_person':
			return {
				body: 'Hybrid online & in-person',
				popoverCopy:
					'The program is delivered both on the internet and onsite at learning provider or other site.',
			};
		case 'in_person':
			return {
				body: 'In-person',
				popoverCopy:
					"The program is delivered at your school's location and not over the internet.",
			};
		default:
			return {
				body: '',
				popoverCopy: '',
			};
	}
};
